import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Fabric, fabricTypes, fabricClasses, dyeingTypes,
  fabricClassLabelToValue, fabricTypeLabelToValue, dyeingTypeLabelToValue,
  currencies, priceUnits, priceUnitValueToLabel
} from '../models/Fabric';
import { createFabric } from '../services/fabricService';
import { toast } from 'react-toastify';
import { generateFabricCode } from '../utils/fabricCodeGenerator';

interface FabricWithPricing extends Fabric {
  price?: number;
  currency?: string;
  priceUnit?: string;
}

interface CreateFabricProps {
  onSuccess?: (fabric: Fabric) => void;
  onCancel?: () => void;
  isDrawerMode?: boolean;
}

const CreateFabric: React.FC<CreateFabricProps> = ({ 
  onSuccess, 
  onCancel, 
  isDrawerMode = false 
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [form, setForm] = useState<Partial<FabricWithPricing>>({
    name: '',
    fabricClass: fabricClasses[0],
    type: fabricTypes[0],
    yarnNumber: '',
    composition: '',
    dyeingType: dyeingTypes[0],
    density: {
      warp: '',
      weft: ''
    },
    weight: 0,
    width: 0,
    notes: '',
    price: 0,
    currency: 'TRY',
    priceUnit: 'meter'
  });

  // Dosya yükleme için state'ler
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [swatchFile, setSwatchFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [swatchPreview, setSwatchPreview] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Sıklık (density) bilgileri için özel işleme
    if (name === 'warp' || name === 'weft') {
      setForm(prev => ({
        ...prev,
        density: {
          ...prev.density,
          [name]: value
        }
      }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
  };

  // Resim yükleme
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Kumaş örneği yükleme
  const handleSwatchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSwatchFile(file);
      setSwatchPreview(URL.createObjectURL(file));
    }
  };

  // Dosya kaldırma işlemleri
  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };
  
  const handleRemoveSwatch = () => {
    setSwatchFile(null);
    setSwatchPreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Form validasyonu
    if (!form.name || !form.fabricClass || !form.type || !form.composition) {
      toast.error('Lütfen gerekli alanları doldurun.');
      return;
    }

    try {
      setLoading(true);
      console.log("[CreateFabric] Form verisi hazırlanıyor...");
      
      // Kumaş kodunu oluştur
      const fabricCode = generateFabricCode(
        fabricClassLabelToValue(form.fabricClass || fabricClasses[0]),
        form.type || '',
        dyeingTypeLabelToValue(form.dyeingType || dyeingTypes[0])
      );

      // Form verilerini eksiksiz hazırla
      const fabricDataForApi: Partial<FabricWithPricing> = {
        ...form,
        code: fabricCode,
        fabricClass: fabricClassLabelToValue(form.fabricClass || fabricClasses[0]),
        type: fabricTypeLabelToValue(form.type || fabricTypes[0]),
        dyeingType: dyeingTypeLabelToValue(form.dyeingType || dyeingTypes[0]),
        density: {
          warp: form.density?.warp || '',
          weft: form.density?.weft || ''
        },
        weight: typeof form.weight === 'number' ? form.weight : 0,
        width: typeof form.width === 'number' ? form.width : 0,
        notes: form.notes || '',
        price: typeof form.price === 'number' ? form.price : 0,
        currency: form.currency || 'TRY',
        priceUnit: form.priceUnit || 'meter'
      };
      
      console.log('[CreateFabric] Kumaş kodu oluşturuldu:', fabricCode);
      console.log('[CreateFabric] API\'ye gönderilecek veri:', JSON.stringify(fabricDataForApi, null, 2));
      
      const newFabricFromApi = await createFabric(fabricDataForApi, imageFile || undefined, swatchFile || undefined);
      toast.success('Kumaş başarıyla eklendi');
      
      if (onSuccess) {
        onSuccess(newFabricFromApi); 
      } else if (!isDrawerMode) {
        navigate('/fabrics');
      }
    } catch (error: any) {
      console.error('[CreateFabric] Hata oluştu:', error);
      toast.error(`Kumaş eklenirken bir hata oluştu: ${error.message || 'Bilinmeyen hata'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${isDrawerMode ? '' : 'container mx-auto px-4 py-8'}`}>
      {!isDrawerMode && (
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/fabrics')}
            className="flex items-center mr-4 text-blue-600 hover:text-blue-800 transition duration-200"
            disabled={loading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            <span>Geri</span>
          </button>
          <h1 className="text-2xl font-bold text-gray-800">Yeni Kumaş Ekle</h1>
        </div>
      )}

      <form 
        onSubmit={handleSubmit} 
        encType="multipart/form-data"
        className="bg-white shadow-md rounded-lg p-6 mb-6 border border-gray-200"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Temel Bilgiler */}
          <div className="p-4 bg-blue-50/50 rounded-lg border border-blue-100 transition-all duration-200 hover:shadow-sm">
            <h2 className="text-lg font-semibold mb-4 text-blue-800 border-b border-blue-200 pb-2 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Temel Bilgiler
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1 flex items-center">
                  Kumaş Adı 
                  <span className="text-red-600 ml-1">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                  required
                  placeholder="Kumaş adını girin"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1 flex items-center">
                  Kumaş Sınıfı 
                  <span className="text-red-600 ml-1">*</span>
                </label>
                <select
                  name="fabricClass"
                  value={form.fabricClass}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition duration-200"
                  required
                >
                  {fabricClasses.map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1 flex items-center">
                  Kumaş Tipi (kumaş kalitesi) 
                  <span className="text-red-600 ml-1">*</span>
                </label>
                <select
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition duration-200"
                  required
                >
                  {fabricTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1 flex items-center">
                  İplik Numarası
                </label>
                <input
                  type="text"
                  name="yarnNumber"
                  value={form.yarnNumber}
                  onChange={handleChange}
                  placeholder="Örn: 30/1, 40/2"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1 flex items-center">
                  İçerik Bilgisi
                  <span className="text-red-600 ml-1">*</span>
                </label>
                <input
                  type="text"
                  name="composition"
                  value={form.composition}
                  onChange={handleChange}
                  placeholder="Örn: %100 Pamuk, %80 Polyester %20 Elastan"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Boyama Çeşidi
                </label>
                <select
                  name="dyeingType"
                  value={form.dyeingType}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition duration-200"
                >
                  {dyeingTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Teknik ve Diğer Bilgiler */}
          <div className="p-4 bg-gray-50/70 rounded-lg border border-gray-200 transition-all duration-200 hover:shadow-sm">
            <h2 className="text-lg font-semibold mb-4 text-gray-800 border-b border-gray-200 pb-2 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
              Teknik Bilgiler
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Sıklık Bilgisi
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-600 text-xs mb-1">
                      Çözgü
                    </label>
                    <input
                      type="text"
                      name="warp"
                      value={form.density?.warp || ''}
                      onChange={handleChange}
                      placeholder="Tel/cm"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-600 text-xs mb-1">
                      Atkı
                    </label>
                    <input
                      type="text"
                      name="weft"
                      value={form.density?.weft || ''}
                      onChange={handleChange}
                      placeholder="Tel/cm"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    Gramaj (gr/m²)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="weight"
                      value={form.weight || ''}
                      onChange={handleNumberChange}
                      min="0"
                      step="0.1"
                      placeholder="0.0"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                    />
                    <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-gray-500">
                      gr/m²
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    Kumaş Eni
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="width"
                      value={form.width || ''}
                      onChange={handleNumberChange}
                      min="0"
                      placeholder="0"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                    />
                    <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-gray-500">
                      cm
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    Notlar
                  </label>
                  <textarea
                    name="notes"
                    value={form.notes || ''}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Kumaş ile ilgili notlar..."
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                  />
                </div>

                {/* Fiyat Bilgileri */}
                <div className="pt-4 border-t border-gray-200">
                  <h3 className="text-md font-medium mb-3 text-gray-800 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                    </svg>
                    Fiyat Bilgileri
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">
                        Kumaş Fiyatı
                      </label>
                      <input
                        type="number"
                        name="price"
                        value={form.price || ''}
                        onChange={handleNumberChange}
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">
                        Para Birimi
                      </label>
                      <select
                        name="currency"
                        value={form.currency}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition duration-200"
                      >
                        {currencies.map(currency => (
                          <option key={currency} value={currency}>{currency}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">
                        Fiyat Birimi
                      </label>
                      <select
                        name="priceUnit"
                        value={form.priceUnit}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition duration-200"
                      >
                        {priceUnits.map(unit => (
                          <option key={unit} value={unit}>{priceUnitValueToLabel(unit)}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {form.price && form.currency && form.priceUnit ? (
                    <div className="mt-2 text-sm text-gray-600">
                      Fiyat: {form.price} {currencies.find(c => c.value === form.currency)?.label} / {priceUnits.find(u => u.value === form.priceUnit)?.label}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-8 space-x-2 border-t pt-6">
          <button
            type="button"
            onClick={onCancel || (() => navigate('/fabrics'))}
            className="px-5 py-2.5 bg-white hover:bg-gray-100 border border-gray-300 text-gray-700 font-medium rounded-md shadow-sm transition duration-200 flex items-center"
            disabled={loading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            İptal
          </button>
          <button
            type="submit"
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm transition duration-200 flex items-center"
            disabled={loading}
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Kaydediliyor...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Kaydet
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateFabric;
